// src/prototype/components/oir/report/OIRSectionStrategicContext.tsx

import React from 'react';
import { Target, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { DEMO_CLIENT } from '../../../config/demoClient';
import { useCoverage } from '../../../hooks/useCoverage';
import { personalizationPercent, personalizationBand } from '../../../../lib/coverage/personalizationPercent';

interface OIRSectionStrategicContextProps {
  clientName?: string;
}

// ── Static content (mirrors existing OIRStrategicContext) ─────────────────
const STRATEGIC_PRIORITIES = [
  'Reduce Cost per Adjusted Discharge',
  'Improve Operating Margin',
  'Accelerate Revenue Cycle Performance',
  'Reduce Avoidable Readmissions',
  'Improve Staff Retention & Productivity',
];

const KNOWN_CONSTRAINTS = [
  'EHR migration underway — IT bandwidth limited through Q3',
  'Limited data science FTEs; vendor support required for complex builds',
  'Board approval required for investments >$500K',
];

export const OIRSectionStrategicContext: React.FC<OIRSectionStrategicContextProps> = ({
  clientName = DEMO_CLIENT.name,
}) => {
  // Read directly rather than through a prop. Both mounts (OIRReportView and the printable
  // report) previously passed the same seeded vector, which meant two call sites had to be
  // kept in step with one number. It is derived, so there is nothing to thread.
  const { aggregates } = useCoverage();
  const personalizationPct = personalizationPercent(aggregates);
  const band = personalizationBand(personalizationPct);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">
      {/* Section header */}
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Strategic Context</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          Organizational profile, priorities, and known constraints shaping this analysis
        </p>
      </div>

      {/* Narrative block */}
      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The analysis is calibrated to <strong>{clientName}</strong>'s organizational profile,
        strategic priorities, and known constraints. Opportunity rankings reflect the depth and
        specificity of the inputs provided. A measure a document evidences carries more weight
        than the same measure answered in a survey, and a measure nothing reaches runs on a
        library default.
      </p>

      {/* 2-column: priorities + constraints */}
      <div className="grid grid-cols-1 gap-4 mb-5 sm:grid-cols-2">
        {/* Left — Strategic Priorities */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <Target className="w-3.5 h-3.5 text-[#18365E]" />
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              Strategic Priorities
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            {STRATEGIC_PRIORITIES.map((p) => (
              <div key={p} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0 mt-0.5" />
                <span className="text-xs text-[#18365E] leading-snug">{p}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Known Constraints */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              Known Constraints
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {KNOWN_CONSTRAINTS.map((c) => (
              <div
                key={c}
                className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
              >
                <p className="text-xs text-amber-800 leading-snug">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Personalization callout. One number, derived by tier weight over the 38 measures:
          a printed report is the wrong place for a diagnostic table. */}
      <div className="bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
            Personalization Level
          </p>
          <span className={`text-xs font-bold ${band.color}`}>
            {band.label} ({personalizationPct}%)
          </span>
        </div>
        <p className="text-[10px] text-[#717182] leading-snug mt-2">
          How strongly this organization's own inputs inform the scoring measures. A measure
          a document evidences counts for more than the same measure answered in a survey.
        </p>
      </div>
    </div>
  );
};